import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { Download, Plus, CreditCard, MoreHorizontal } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { API_BASE } from "@/config/api"
import { formatRon, formatDate } from "@/lib/format"
import { toast } from "sonner"

const API = API_BASE

const statusStyle = {
  paid: "bg-secondary text-secondary-foreground",
  unpaid: "bg-accent/15 text-accent",
  overdue: "bg-destructive/15 text-destructive",
}

const emptyLine = () => ({ description: "", quantity: "1", unit_price: "250" })

export default function Billing() {
  const { t } = useTranslation()
  const [invoices, setInvoices] = useState([])
  const [clients, setClients] = useState([])
  const [filter, setFilter] = useState("all")
  const [newOpen, setNewOpen] = useState(false)
  const [form, setForm] = useState({
    client_id: "",
    amount: "250",
    vat_rate: "0",
    due_days: "14",
    invoice_notes: "",
  })
  const [lines, setLines] = useState([emptyLine()])
  const [insights, setInsights] = useState(null)
  const [plans, setPlans] = useState([])
  const [subs, setSubs] = useState([])
  const [planForm, setPlanForm] = useState({ name: "", amount: "800", sessions: "4" })
  const [assignForm, setAssignForm] = useState({ client_id: "", plan_id: "" })
  const [params] = useSearchParams()

  const load = () => {
    axios.get(`${API}/invoices`).then((r) => setInvoices(r.data)).catch(() => {})
    axios.get(`${API}/clients`).then((r) => setClients(r.data)).catch(() => {})
    axios.get(`${API}/billing/insights`).then((r) => setInsights(r.data)).catch(() => {})
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data)).catch(() => setPlans([]))
    axios.get(`${API}/billing/subscriptions`).then((r) => setSubs(r.data)).catch(() => setSubs([]))
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    setPlanForm((p) => (p.name ? p : { ...p, name: t("app.billing.defaultPlanName") }))
  }, [t])
  useEffect(() => {
    const f = params.get("filter")
    if (f) setFilter(f)
    if (params.get("action") === "new") setNewOpen(true)
  }, [params])

  const monthStart = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const filtered = filter === "all" ? invoices : invoices.filter((i) => i.status === filter)

  const paidThisMonth = invoices
    .filter((i) => i.status === "paid" && new Date(i.issued_at) >= monthStart)
    .reduce((s, i) => s + i.amount, 0)

  const totals = {
    paid: paidThisMonth,
    unpaid: invoices.filter((i) => i.status === "unpaid").reduce((s, i) => s + i.amount, 0),
    overdue: invoices.filter((i) => i.status === "overdue").reduce((s, i) => s + i.amount, 0),
  }

  const lineTotal = useMemo(() => {
    return lines.reduce((s, l) => {
      const q = parseFloat(l.quantity) || 0
      const u = parseFloat(l.unit_price) || 0
      return s + q * u
    }, 0)
  }, [lines])

  const createInvoice = async () => {
    const client = clients.find((c) => c.id === form.client_id)
    if (!client) return toast.error(t("app.billing.toast.selectClient"))
    const lineItems = lines
      .filter((l) => (l.description || "").trim() && (parseFloat(l.unit_price) || 0) > 0)
      .map((l) => ({
        description: l.description.trim(),
        quantity: parseFloat(l.quantity) || 1,
        unit_price: parseFloat(l.unit_price) || 0,
        vat_rate: parseFloat(form.vat_rate) || 0,
      }))
    const amount = lineItems.length
      ? lineItems.reduce((s, l) => s + l.quantity * l.unit_price, 0)
      : parseFloat(form.amount)
    if (!amount || amount <= 0) return toast.error(t("app.billing.toast.error"))
    try {
      await axios.post(`${API}/invoices`, {
        client_id: client.id,
        client_name: client.name,
        amount: Math.round(amount * 100) / 100,
        due_days: parseInt(form.due_days, 10) || 14,
        vat_rate: parseFloat(form.vat_rate) || 0,
        invoice_notes: form.invoice_notes || "",
        line_items: lineItems.length ? lineItems : undefined,
      })
      setNewOpen(false)
      setForm({ client_id: "", amount: "250", vat_rate: "0", due_days: "14", invoice_notes: "" })
      setLines([emptyLine()])
      load()
      toast.success(t("app.billing.toast.invoiceCreated"))
    } catch {
      toast.error(t("app.billing.toast.error"))
    }
  }

  const exportAnaf = async () => {
    try {
      const { data } = await axios.get(`${API}/reports/anaf`)
      const blob = new Blob([data.csv], { type: "text/csv" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "export-anaf.csv"
      a.click()
      toast.success(t("app.billing.toast.anafExported"))
    } catch {
      toast.error(t("app.billing.toast.exportFailed"))
    }
  }

  const remind = async (id) => {
    try {
      const { data } = await axios.post(`${API}/invoices/${id}/remind`)
      if (data.sent) {
        toast.success(t("app.billing.toast.reminderSent", { to: data.to || "" }))
      } else {
        toast.message(
          data.message
            ? t("app.billing.toast.reminderDraft", { preview: data.message.slice(0, 100) })
            : t("app.billing.toast.reminderDraftOnly")
        )
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.billing.toast.reminderFailed"))
    }
  }

  const efactura = async (id) => {
    try {
      let data
      try {
        ;({ data } = await axios.post(`${API}/invoices/${id}/efactura-submit`))
      } catch {
        ;({ data } = await axios.post(`${API}/invoices/${id}/efactura`))
      }
      toast.success(t("app.billing.toast.efacturaOk", { id: data.efactura_id }))
      load()
    } catch {
      toast.error(t("app.billing.toast.efacturaFailed"))
    }
  }

  const paymentLink = async (id) => {
    try {
      let data
      try {
        ;({ data } = await axios.post(`${API}/invoices/${id}/stripe-checkout`))
      } catch (e) {
        if (e.response?.status === 503 || e.response?.status === 400) {
          toast.error(e.response?.data?.detail || t("app.billing.toast.stripeUnavailable"))
          return
        }
        ;({ data } = await axios.post(`${API}/invoices/${id}/payment-link`))
      }
      if (data.demo) {
        toast.message(t("app.billing.toast.stripeDemo"))
      }
      const url = data.url || data.payment_link
      if (!url) return toast.error(t("app.billing.toast.paymentFailed"))
      if (data.url && !data.demo) window.open(data.url, "_blank")
      else await navigator.clipboard.writeText(url)
      toast.success(
        data.demo
          ? t("app.billing.toast.demoLinkCopied")
          : data.url
            ? t("app.billing.toast.stripeOpened")
            : t("app.billing.toast.payLinkCopied")
      )
      load()
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.billing.toast.paymentFailed"))
    }
  }

  const downloadPdf = async (id) => {
    try {
      const { data } = await axios.get(`${API}/invoices/${id}/pdf`)
      if (data.pdf_base64) {
        const a = document.createElement("a")
        a.href = `data:application/pdf;base64,${data.pdf_base64}`
        a.download = `factura-${id.slice(0, 8)}.pdf`
        a.click()
        toast.success(t("app.billing.toast.pdfDownloaded"))
        return
      }
      const w = window.open("", "_blank")
      if (w && data.html) {
        w.document.write(data.html)
        w.document.close()
        w.print()
      }
    } catch {
      toast.error(t("app.billing.toast.pdfUnavailable"))
    }
  }

  const downloadXml = async (id) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}/efactura-xml`, { responseType: "blob" })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement("a")
      a.href = url
      a.download = `efactura-${id.slice(0, 8)}.xml`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(t("app.billing.toast.xmlDownloaded"))
    } catch {
      toast.error(t("app.billing.toast.xmlUnavailable"))
    }
  }

  const copyPayLink = async (inv) => {
    const token = inv.pay_token
    if (!token) return toast.error(t("app.billing.toast.payTokenUnavailable"))
    const url = `${window.location.origin}/pay/${token}`
    await navigator.clipboard.writeText(url)
    toast.success(t("app.billing.toast.payLinkCopied"))
  }

  const createPlan = async () => {
    try {
      await axios.post(`${API}/billing/plans`, {
        name: planForm.name,
        amount_ron: parseFloat(planForm.amount),
        sessions_per_month: parseInt(planForm.sessions, 10),
      })
      load()
      toast.success(t("app.billing.toast.planCreated"))
    } catch {
      toast.error(t("app.billing.toast.planFailed"))
    }
  }

  const assignPlan = async () => {
    if (!assignForm.client_id || !assignForm.plan_id) {
      return toast.error(t("app.billing.toast.selectClientAndPlan"))
    }
    try {
      await axios.post(`${API}/clients/${assignForm.client_id}/subscriptions`, {
        plan_id: assignForm.plan_id,
      })
      setAssignForm({ client_id: "", plan_id: "" })
      load()
      toast.success(t("app.billing.toast.subscriptionAssigned"))
    } catch {
      toast.error(t("app.billing.toast.subscriptionFailed"))
    }
  }

  const setSubStatus = async (subId, status) => {
    try {
      await axios.patch(`${API}/billing/subscriptions/${subId}`, { status })
      load()
      toast.success(t("app.billing.toast.subscriptionUpdated"))
    } catch {
      toast.error(t("app.billing.toast.subscriptionFailed"))
    }
  }

  const filterLabel = (f) => {
    if (f === "all") return t("app.billing.filterAll")
    if (f === "paid") return t("app.billing.filterPaid")
    if (f === "unpaid") return t("app.billing.filterUnpaid")
    if (f === "overdue") return t("app.billing.filterOverdue")
    return f
  }

  return (
    <AppLayout
      title={t("app.billing.title")}
      actions={
        <>
          <Button variant="outline" size="sm" className="rounded-full" data-testid="billing-export" onClick={exportAnaf}>
            <Download strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.billing.exportAnaf")}
          </Button>
          <Button size="sm" className="rounded-full" data-testid="billing-new" onClick={() => setNewOpen(true)}>
            <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.billing.newInvoice")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {[
          { label: t("app.billing.collectedMonth"), v: totals.paid, tint: "" },
          { label: t("app.billing.outstanding"), v: totals.unpaid, tint: "text-accent" },
          { label: t("app.billing.overdue"), v: totals.overdue, tint: "text-destructive" },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{c.label}</p>
            <p className={`font-display text-3xl tracking-tight font-medium mt-2 ${c.tint}`}>{formatRon(c.v)}</p>
          </div>
        ))}
      </div>

      {plans.length > 0 && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t("app.billing.recurringPlans")}</p>
          <div className="flex flex-wrap gap-3 text-sm">
            {plans.map((p) => (
              <span key={p.id} className="px-3 py-1 rounded-full bg-secondary">
                {t("app.billing.planSessions", {
                  name: p.name,
                  amount: formatRon(p.amount_ron),
                  sessions: p.sessions_per_month,
                })}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 rounded-xl border border-dashed border-border p-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label>{t("app.billing.planName")}</Label>
          <Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} className="mt-1 w-40" />
        </div>
        <div>
          <Label>{t("app.billing.planAmount")}</Label>
          <Input type="number" value={planForm.amount} onChange={(e) => setPlanForm({ ...planForm, amount: e.target.value })} className="mt-1 w-24" />
        </div>
        <div>
          <Label>{t("app.billing.planSessionsLabel")}</Label>
          <Input type="number" value={planForm.sessions} onChange={(e) => setPlanForm({ ...planForm, sessions: e.target.value })} className="mt-1 w-20" />
        </div>
        <Button variant="outline" className="rounded-full" onClick={createPlan}>{t("app.billing.addPlan")}</Button>
      </div>

      <div className="mb-6 rounded-xl border border-border bg-card p-4 space-y-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("app.billing.assignPlanTitle")}</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[160px]">
            <Label>{t("app.billing.table.client")}</Label>
            <Select value={assignForm.client_id} onValueChange={(v) => setAssignForm({ ...assignForm, client_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder={t("app.billing.table.client")} /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label>{t("app.billing.planName")}</Label>
            <Select value={assignForm.plan_id} onValueChange={(v) => setAssignForm({ ...assignForm, plan_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder={t("app.billing.planName")} /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button className="rounded-full" onClick={assignPlan} disabled={!plans.length}>{t("app.billing.assignPlan")}</Button>
        </div>
        {subs.length > 0 && (
          <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
            {subs.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{s.client_name}</span>
                  <span className="text-muted-foreground"> — {s.plan_name} ({formatRon(s.amount_ron)})</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-secondary" : "bg-muted"}`}>
                    {t(`app.billing.subStatus.${s.status}`, { defaultValue: s.status })}
                  </span>
                </div>
                <div className="flex gap-1">
                  {s.status === "active" ? (
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setSubStatus(s.id, "paused")}>
                      {t("app.billing.pauseSub")}
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setSubStatus(s.id, "active")}>
                      {t("app.billing.resumeSub")}
                    </Button>
                  )}
                  {s.status !== "cancelled" && (
                    <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive" onClick={() => setSubStatus(s.id, "cancelled")}>
                      {t("app.billing.cancelSub")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {insights?.per_client?.length > 0 && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t("app.billing.topClients")}</p>
          <div className="flex flex-wrap gap-3 text-sm">
            {insights.per_client.slice(0, 5).map((c) => (
              <span key={c.client_id} className="px-3 py-1 rounded-full bg-secondary">{c.name}: {formatRon(c.revenue)}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 flex gap-2" data-testid="billing-filters">
        {["all", "paid", "unpaid", "overdue"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`text-xs px-4 py-1.5 rounded-full capitalize transition-colors ${
              filter === f ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {filterLabel(f)}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.16em] text-muted-foreground bg-muted/40">
              <th className="text-left font-semibold px-6 py-3">{t("app.billing.table.invoice")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("app.billing.table.client")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("app.billing.table.issued")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("app.billing.table.due")}</th>
              <th className="text-right font-semibold px-6 py-3">{t("app.billing.table.amount")}</th>
              <th className="text-right font-semibold px-6 py-3">{t("app.billing.table.status")}</th>
              <th className="text-right font-semibold px-6 py-3">{t("app.billing.table.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid="invoices-table">
            {filtered.map((i) => (
              <tr key={i.id} className="hover:bg-muted/40 transition-colors">
                <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{i.number}</td>
                <td className="px-6 py-4 font-medium">{i.client_name}</td>
                <td className="px-6 py-4 text-muted-foreground">{formatDate(i.issued_at)}</td>
                <td className="px-6 py-4 text-muted-foreground">{formatDate(i.due_at)}</td>
                <td className="px-6 py-4 text-right font-medium">{formatRon(i.amount)}</td>
                <td className="px-6 py-4 text-right">
                  <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${statusStyle[i.status]}`}>{i.status}</span>
                  {i.efactura_status && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {t("app.billing.table.efactura", { status: i.efactura_status })}
                    </p>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-1 items-center">
                    {i.status !== "paid" && (
                      <Button size="sm" className="text-xs h-8 gap-1 rounded-full" onClick={() => paymentLink(i.id)}>
                        <CreditCard className="h-3 w-3" /> {t("app.billing.pay")}
                      </Button>
                    )}
                    {!i.efactura_id && (
                      <Button variant="outline" size="sm" className="text-xs h-8 rounded-full" onClick={() => efactura(i.id)}>
                        {t("app.billing.efacturaShort")}
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t("app.billing.table.actions")}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {i.status !== "paid" && (
                          <DropdownMenuItem onClick={() => remind(i.id)}>{t("app.billing.reminderTitle")}</DropdownMenuItem>
                        )}
                        {i.pay_token && i.status !== "paid" && (
                          <DropdownMenuItem onClick={() => copyPayLink(i)}>{t("app.billing.payLinkTitle")}</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => downloadPdf(i.id)}>{t("app.billing.pdfTitle")}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadXml(i.id)}>{t("app.billing.xmlTitle")}</DropdownMenuItem>
                        {!i.efactura_id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => efactura(i.id)}>{t("app.billing.efacturaTitle")}</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">{t("app.billing.table.empty")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("app.billing.newDialogTitle")}</DialogTitle></DialogHeader>
          <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
            <SelectTrigger><SelectValue placeholder={t("app.billing.table.client")} /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("app.billing.lineItems")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                {t("app.billing.addLine")}
              </Button>
            </div>
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6">
                  {idx === 0 && <Label className="text-xs">{t("app.billing.lineDescription")}</Label>}
                  <Input
                    value={line.description}
                    onChange={(e) => {
                      const next = [...lines]
                      next[idx] = { ...next[idx], description: e.target.value }
                      setLines(next)
                    }}
                    placeholder={t("app.billing.lineDescription")}
                  />
                </div>
                <div className="col-span-2">
                  {idx === 0 && <Label className="text-xs">{t("app.billing.lineQty")}</Label>}
                  <Input
                    type="number"
                    value={line.quantity}
                    onChange={(e) => {
                      const next = [...lines]
                      next[idx] = { ...next[idx], quantity: e.target.value }
                      setLines(next)
                    }}
                  />
                </div>
                <div className="col-span-3">
                  {idx === 0 && <Label className="text-xs">{t("app.billing.linePrice")}</Label>}
                  <Input
                    type="number"
                    value={line.unit_price}
                    onChange={(e) => {
                      const next = [...lines]
                      next[idx] = { ...next[idx], unit_price: e.target.value }
                      setLines(next)
                    }}
                  />
                </div>
                <div className="col-span-1">
                  {lines.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 px-1"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      ×
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {lineTotal > 0 && (
              <p className="text-xs text-muted-foreground text-right">
                {t("app.billing.lineTotal")}: {formatRon(lineTotal)}
              </p>
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label>{t("app.billing.amountRon")}</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                disabled={lineTotal > 0}
              />
            </div>
            <div>
              <Label>{t("app.billing.vatRate")}</Label>
              <Input
                type="number"
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("app.billing.dueDays")}</Label>
              <Input
                type="number"
                value={form.due_days}
                onChange={(e) => setForm({ ...form, due_days: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("app.billing.notes")}</Label>
              <Input
                value={form.invoice_notes}
                onChange={(e) => setForm({ ...form, invoice_notes: e.target.value })}
              />
            </div>
          </div>
          <Button className="w-full rounded-full mt-4" onClick={createInvoice}>{t("app.billing.create")}</Button>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
